import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import PublicSiteHeader from '@components/layout/PublicSiteHeader';
import SiteFooter from '@components/layout/SiteFooter';
import { ContestAgeCategoryBar } from '@components/contest/ContestWorkspaceChrome';
import { brandColors } from '@config/brandTokens';
import { getContestSports } from '@utils/sports';
import { getSportIcon } from '@utils/sportPresentation';
import { isAdultContestAge } from '@utils/contestCatalog';
import {
  adultAgesForOrg,
  defaultAgeForOrg,
  orgNoun,
  orgRosterLabel,
  orgTypeLabel,
  youthAgesForOrg,
  youthBandLabel,
} from '@utils/orgTypes';
import { playerPublicPath } from '@utils/publicProfileSlug';
import { VideoReactionBar, getPublicVisitorId } from '@components/profile/VideoReactionBar';

interface AcademyTopper {
  rank: number;
  playerName?: string;
  firstName?: string;
  lastName?: string;
  playerIdCode?: string;
  publicSlug?: string;
  videoUrl?: string;
  videoKey?: string;
  ageCategory?: string;
  totalScore?: number;
  avgScore?: number | null;
  submissionCount?: number;
}

interface PublicProfilePerson {
  id: string;
  firstName?: string;
  lastName?: string;
  playerIdCode?: string;
  coachIdCode?: string;
  publicSlug?: string;
  primarySport?: string;
  secondarySport?: string;
  ageCategory?: string;
  coachLevel?: string;
  level?: string;
}

interface PublicProfileResponse {
  academy: {
    academyIdCode: string;
    name: string;
    description?: string;
    country?: string;
    city?: string;
    academyType?: string;
    bannerUrl?: string;
    publicSlug?: string;
  };
  sport: string;
  ageCategory: string;
  availableSports?: string[];
  toppers?: AcademyTopper[];
  coaches: PublicProfilePerson[];
  students: PublicProfilePerson[];
  stats: {
    coachCount: number;
    studentCount: number;
    topperCount?: number;
    rosterCount?: number;
    contestantCount?: number;
    sportCounts?: Array<{ sport: string; contestants: number }>;
  };
  dashboard?: {
    rosterCount: number;
    coachCount: number;
    contestantCount: number;
    topperCount: number;
    sportCounts?: Array<{ sport: string; contestants: number }>;
  };
  reactions?: Record<string, { counts?: Record<string, number>; mine?: Record<string, boolean> }>;
}

const displayName = (person: { firstName?: string; lastName?: string; playerName?: string }) => {
  const joined = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return joined || person.playerName || 'Participant';
};

const medalColor = (rank: number) => {
  if (rank === 1) return { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' };
  if (rank === 2) return { bg: '#F1F5F9', border: '#94A3B8', text: '#334155' };
  if (rank === 3) return { bg: '#FFEDD5', border: '#FB923C', text: '#9A3412' };
  return { bg: brandColors.white, border: '#E2E8F0', text: brandColors.navy };
};

const PublicAcademyProfilePage: React.FC = () => {
  const { academyCode, slug } = useParams();
  const profileCode = academyCode || slug || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const sports = useMemo(() => getContestSports(), []);
  const selectedSport = searchParams.get('sport') || sports[0] || 'Football';
  const ageFromUrl = searchParams.get('age');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<PublicProfileResponse | null>(null);

  const academyType = profile?.academy.academyType || 'regular_school';
  const isSportsAcademy = academyType === 'sports_academy';
  const selectedAge = ageFromUrl || profile?.ageCategory || defaultAgeForOrg(academyType);
  const adultAges = adultAgesForOrg(academyType);
  const ageBand: 'youth' | 'adult' = isAdultContestAge(selectedAge) ? 'adult' : 'youth';
  const visibleAges = ageBand === 'youth' ? youthAgesForOrg(academyType) : adultAges;

  const updateFilters = (next: { sport?: string; age?: string }) => {
    const params = new URLSearchParams(searchParams);
    params.set('sport', next.sport || selectedSport);
    params.set('age', next.age || selectedAge);
    setSearchParams(params, { replace: true });
  };

  const handleBandChange = (band: 'youth' | 'adult') => {
    const nextAge = band === 'youth' ? defaultAgeForOrg(academyType) : 'Senior';
    updateFilters({ age: nextAge });
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!profileCode) {
        setError('Academy code is required');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const query = new URLSearchParams({ sport: selectedSport, visitorId: getPublicVisitorId() });
        if (ageFromUrl) query.set('ageCategory', ageFromUrl);
        const response = await fetch(`/api/public/academy-profile/${encodeURIComponent(profileCode)}?${query}`);
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Failed to load academy profile');
        }
        setProfile(data.data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load academy profile');
      } finally {
        setLoading(false);
      }
    };
    void loadProfile();
  }, [profileCode, selectedSport, ageFromUrl]);

  const locationLabel = [profile?.academy.city, profile?.academy.country].filter(Boolean).join(', ');
  const typeLabel = orgTypeLabel(academyType);
  const noun = orgNoun(academyType);
  const rosterLabel = orgRosterLabel(academyType);
  const bannerUrl = profile?.academy.bannerUrl || '';
  const sportCoaches = useMemo(() => {
    const list = profile?.coaches || [];
    const sportKey = selectedSport.toLowerCase();
    const matched = list.filter((coach) =>
      [coach.primarySport, coach.secondarySport].some((item) => String(item || '').toLowerCase() === sportKey)
    );
    return matched.length > 0 ? matched : list;
  }, [profile?.coaches, selectedSport]);
  const showCoaches = isSportsAcademy || sportCoaches.length > 0;
  const showPlayers = selectedAge !== 'Coach';
  const dashboard = profile?.dashboard || {
    rosterCount: profile?.stats.rosterCount || profile?.stats.studentCount || 0,
    coachCount: profile?.stats.coachCount || 0,
    contestantCount: profile?.stats.contestantCount || 0,
    topperCount: profile?.toppers?.length || profile?.stats.topperCount || 0,
    sportCounts: profile?.stats.sportCounts || [],
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brandColors.ice, display: 'flex', flexDirection: 'column' }}>
      <PublicSiteHeader />

      {loading && !profile ? (
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center', flex: 1 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1.5, color: brandColors.slate }}>Loading public profile...</Typography>
        </Container>
      ) : error ? (
        <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
          <Card>
            <CardContent>
              <Typography color="error" sx={{ fontWeight: 700 }}>
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      ) : profile ? (
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 180, md: 260 },
              background: bannerUrl
                ? `center / cover no-repeat url(${bannerUrl})`
                : 'linear-gradient(135deg, #111739 0%, #1e3a5f 55%, #4A6FEB 100%)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: bannerUrl
                  ? 'linear-gradient(180deg, rgba(17,23,57,0.15) 0%, rgba(17,23,57,0.78) 100%)'
                  : 'transparent',
              }}
            />
            <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 4, md: 6 } }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
                <Chip
                  label={typeLabel}
                  sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', fontWeight: 800 }}
                />
                <Chip
                  label={profile.academy.academyIdCode}
                  sx={{ bgcolor: brandColors.orange, color: '#fff', fontWeight: 800 }}
                />
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: '#fff',
                  fontFamily: '"Sora", Arial, sans-serif',
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  lineHeight: 1.15,
                }}
              >
                {profile.academy.name}
              </Typography>
              {locationLabel ? (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, color: 'rgba(255,255,255,0.88)' }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {locationLabel}
                  </Typography>
                </Stack>
              ) : null}
              {profile.academy.description ? (
                <Typography variant="body2" sx={{ mt: 1.25, maxWidth: 720, color: 'rgba(255,255,255,0.88)' }}>
                  {profile.academy.description}
                </Typography>
              ) : null}
            </Container>
          </Box>

          <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 3.5 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={3}>
                <Card sx={{ border: '1px solid #e2e8f0', position: { md: 'sticky' }, top: { md: 88 } }}>
                  <CardContent>
                    <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 1.25 }}>
                      Sports
                    </Typography>
                    <Stack spacing={0.75}>
                      {sports.map((sportName) => {
                        const selected = selectedSport.toLowerCase() === sportName.toLowerCase();
                        return (
                          <Button
                            key={sportName}
                            onClick={() => updateFilters({ sport: sportName })}
                            startIcon={getSportIcon(sportName, 22)}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              fontWeight: 700,
                              color: selected ? brandColors.white : brandColors.navy,
                              bgcolor: selected ? brandColors.navy : brandColors.ice,
                              border: `1px solid ${selected ? brandColors.navy : '#e2e8f0'}`,
                              '&:hover': {
                                bgcolor: selected ? '#0a0f28' : '#eef2f8',
                              },
                            }}
                          >
                            {sportName}
                          </Button>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={9}>
                <Stack spacing={2}>
                  <Grid container spacing={1.25}>
                    {[
                      { label: 'Contest toppers', value: dashboard.topperCount, icon: <EmojiEventsIcon fontSize="small" />, color: brandColors.orange },
                      { label: 'Contest participants', value: dashboard.contestantCount, icon: <LeaderboardOutlinedIcon fontSize="small" />, color: brandColors.blue },
                      { label: rosterLabel, value: dashboard.rosterCount, icon: <GroupsOutlinedIcon fontSize="small" />, color: brandColors.navy },
                      { label: 'Coaches', value: dashboard.coachCount, icon: <GroupsOutlinedIcon fontSize="small" />, color: '#0f766e' },
                    ].map((card) => (
                      <Grid item xs={6} sm={3} key={card.label}>
                        <Card sx={{ border: '1px solid #e2e8f0', height: '100%' }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ color: card.color, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                              {card.icon}
                              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1, color: brandColors.navy }}>
                                {card.value || 0}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: brandColors.slate }}>
                              {card.label}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  {(dashboard.sportCounts || []).some((item) => item.contestants > 0) ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {(dashboard.sportCounts || []).map((item) => (
                        <Chip
                          key={item.sport}
                          label={`${item.sport}: ${item.contestants}`}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      ))}
                    </Stack>
                  ) : null}

                  <Card sx={{ border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <FormControl>
                        <RadioGroup
                          row
                          value={ageBand}
                          onChange={(event) => handleBandChange(event.target.value as 'youth' | 'adult')}
                        >
                          <FormControlLabel
                            value="youth"
                            control={<Radio sx={{ color: brandColors.navy, '&.Mui-checked': { color: brandColors.orange } }} />}
                            label={<Typography sx={{ fontWeight: 800, color: brandColors.navy }}>{youthBandLabel(academyType)}</Typography>}
                          />
                          <FormControlLabel
                            value="adult"
                            control={<Radio sx={{ color: brandColors.navy, '&.Mui-checked': { color: brandColors.orange } }} />}
                            label={<Typography sx={{ fontWeight: 800, color: brandColors.navy }}>U19+</Typography>}
                          />
                        </RadioGroup>
                      </FormControl>
                    </CardContent>
                  </Card>

                  <ContestAgeCategoryBar
                    sportName={selectedSport}
                    sportIcon={getSportIcon(selectedSport, 24)}
                    ageCategory={selectedAge}
                    categories={visibleAges}
                    onAgeChange={(age) => updateFilters({ age })}
                  />

                  <Card sx={{ border: '1px solid #fed7aa' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <EmojiEventsIcon sx={{ color: brandColors.orange }} />
                        <Typography variant="h6" sx={{ fontWeight: 900, color: brandColors.navy }}>
                          Contest toppers · {selectedSport} · {selectedAge}
                        </Typography>
                      </Stack>
                      {loading ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : !profile.toppers || profile.toppers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No contest participants from this {noun} in {selectedSport} {selectedAge} yet.
                        </Typography>
                      ) : (
                        <Grid container spacing={1.25}>
                          {profile.toppers.map((entry) => {
                            const colors = medalColor(entry.rank);
                            return (
                              <Grid item xs={12} sm={6} key={`${entry.rank}-${entry.playerIdCode || entry.playerName}`}>
                                <Box
                                  sx={{
                                    p: 1.4,
                                    borderRadius: 2,
                                    border: `1px solid ${colors.border}`,
                                    bgcolor: colors.bg,
                                    height: '100%',
                                  }}
                                >
                                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                    <Box
                                      sx={{
                                        minWidth: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        bgcolor: colors.text,
                                        color: '#fff',
                                        display: 'grid',
                                        placeItems: 'center',
                                        fontWeight: 900,
                                      }}
                                    >
                                      {entry.rank}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      {playerPublicPath(entry.publicSlug, entry.playerIdCode) ? (
                                        <Typography
                                          component={RouterLink}
                                          to={playerPublicPath(entry.publicSlug, entry.playerIdCode)}
                                          sx={{ fontWeight: 800, color: brandColors.navy, textDecoration: 'none' }}
                                        >
                                          {displayName(entry)}
                                        </Typography>
                                      ) : (
                                        <Typography sx={{ fontWeight: 800, color: brandColors.navy }}>
                                          {displayName(entry)}
                                        </Typography>
                                      )}
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {[entry.playerIdCode, entry.ageCategory, `${entry.submissionCount || 0} skills`].filter(Boolean).join(' · ')}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.4, fontWeight: 800, color: colors.text }}>
                                        {Number(entry.totalScore || 0).toFixed(1)} pts
                                        {entry.avgScore != null ? ` · avg ${Number(entry.avgScore).toFixed(1)}` : ''}
                                      </Typography>
                                      {entry.videoUrl ? (
                                        <>
                                          <Button
                                            size="small"
                                            startIcon={<SmartDisplayIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => window.open(entry.videoUrl, '_blank', 'noopener,noreferrer')}
                                            sx={{ textTransform: 'none', px: 0, mt: 0.25 }}
                                          >
                                            Watch
                                          </Button>
                                          <VideoReactionBar
                                            videoKey={entry.videoKey}
                                            initial={profile.reactions?.[entry.videoKey || '']}
                                          />
                                        </>
                                      ) : null}
                                    </Box>
                                  </Stack>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>

                  {showCoaches ? (
                    <Card sx={{ border: '1px solid #e2e8f0' }}>
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                          <GroupsOutlinedIcon sx={{ color: brandColors.navy }} />
                          <Typography variant="h6" sx={{ fontWeight: 900, color: brandColors.navy }}>
                            Coaches ({sportCoaches.length})
                          </Typography>
                        </Stack>
                        {sportCoaches.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No coaches published yet.</Typography>
                        ) : (
                          <Grid container spacing={1.1}>
                            {sportCoaches.map((coach) => (
                              <Grid item xs={12} sm={6} key={coach.id}>
                                <Box sx={{ p: 1.2, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                                  <Typography sx={{ fontWeight: 800 }}>{displayName(coach)}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {[coach.coachIdCode, coach.primarySport, coach.secondarySport, coach.coachLevel].filter(Boolean).join(' · ')}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  ) : null}

                  {showPlayers ? (
                    <Card sx={{ border: '1px solid #e2e8f0' }}>
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                          <GroupsOutlinedIcon sx={{ color: brandColors.blue }} />
                          <Typography variant="h6" sx={{ fontWeight: 900, color: brandColors.navy }}>
                            {rosterLabel} · {selectedAge} ({profile.students.length})
                          </Typography>
                        </Stack>
                        {profile.students.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No {selectedAge} {rosterLabel.toLowerCase()} listed for this {noun} yet.
                          </Typography>
                        ) : (
                          <Grid container spacing={1.1}>
                            {profile.students.map((player) => (
                              <Grid item xs={12} sm={6} md={4} key={player.id}>
                                <Box sx={{ p: 1.2, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                                  {playerPublicPath(player.publicSlug, player.playerIdCode) ? (
                                    <Typography
                                      component={RouterLink}
                                      to={playerPublicPath(player.publicSlug, player.playerIdCode)}
                                      sx={{ fontWeight: 800, color: brandColors.navy, textDecoration: 'none' }}
                                    >
                                      {displayName(player)}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ fontWeight: 800 }}>{displayName(player)}</Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {[player.playerIdCode, player.primarySport, player.ageCategory, player.level].filter(Boolean).join(' · ')}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  ) : null}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ) : null}

      <SiteFooter />
    </Box>
  );
};

export default PublicAcademyProfilePage;
